import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Check, KeyRound, Copy, LifeBuoy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { formatPrice } from "@/lib/storefront";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/_account/support-plans")({
  head: () => ({ meta: [{ title: "Support plans — CoderLity" }] }),
  component: SupportPlansPage,
});

type Plan = {
  id: string; name: string; slug: string;
  kind: "free" | "monthly" | "yearly" | "one_time";
  description: string | null;
  price_cents: number; currency: string;
  duration_days: number | null; ticket_quota: number | null; sla_hours: number | null;
  sort_order: number;
};
type Category = { id: string; name: string; slug: string };
type PlanCat = { plan_id: string; category_id: string };
type Sub = {
  id: string; plan_id: string; activation_key: string;
  status: "active" | "expired" | "cancelled";
  starts_at: string; expires_at: string | null;
};

const kindLabel: Record<Plan["kind"], string> = { free: "Free", monthly: "Monthly", yearly: "Yearly", one_time: "One-time" };

function SupportPlansPage() {
  const { user } = useAuth();

  const { data: plans, isLoading } = useQuery({
    queryKey: ["pub-support-plans"],
    queryFn: async () => {
      const { data, error } = await supabase.from("support_plans").select("*").eq("active", true).order("sort_order");
      if (error) throw error;
      return data as Plan[];
    },
  });
  const { data: cats } = useQuery({
    queryKey: ["pub-support-cats"],
    queryFn: async () => {
      const { data, error } = await supabase.from("support_categories").select("id,name,slug").eq("active", true).order("sort_order");
      if (error) throw error;
      return data as Category[];
    },
  });
  const { data: links } = useQuery({
    queryKey: ["pub-support-plan-cats"],
    queryFn: async () => {
      const { data, error } = await supabase.from("support_plan_categories").select("*");
      if (error) throw error;
      return data as PlanCat[];
    },
  });
  const { data: subs } = useQuery({
    enabled: !!user,
    queryKey: ["my-support-subs", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.from("support_subscriptions").select("*").eq("user_id", user!.id).order("created_at", { ascending: false });
      if (error) throw error;
      return data as Sub[];
    },
  });

  const active = subs?.find(s => s.status === "active");
  const catsFor = (planId: string) => (cats ?? []).filter(c => (links ?? []).some(l => l.plan_id === planId && l.category_id === c.id));

  if (isLoading) return <Skeleton className="h-64 w-full" />;

  return (
    <>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Support plans</h1>
          <p className="text-sm text-muted-foreground">Free community help or priority paid support — billed monthly, yearly, or as a one-time activation.</p>
        </div>
        <Button asChild variant="outline" className="gap-2">
          <Link to="/submit-ticket"><LifeBuoy className="h-4 w-4" /> Submit a ticket</Link>
        </Button>
      </div>

      {active && <ActiveSubscriptionCard sub={active} planName={plans?.find(p => p.id === active.plan_id)?.name ?? "Plan"} />}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {(plans ?? []).map(p => {
          const mine = active?.plan_id === p.id;
          return (
            <Card key={p.id} className={mine ? "border-primary" : ""}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <Badge variant="outline">{kindLabel[p.kind]}</Badge>
                  {mine && <Badge>Current</Badge>}
                </div>
                <CardTitle>{p.name}</CardTitle>
                <CardDescription>{p.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <div className="text-2xl font-bold">{p.price_cents === 0 ? "Free" : formatPrice(p.price_cents)}</div>
                  {p.kind === "monthly" && <div className="text-xs text-muted-foreground">per month</div>}
                  {p.kind === "yearly" && <div className="text-xs text-muted-foreground">per year</div>}
                  {p.kind === "one_time" && <div className="text-xs text-muted-foreground">one-time payment</div>}
                </div>
                <ul className="space-y-1 text-sm">
                  <Feature>{p.ticket_quota ? `${p.ticket_quota} tickets` : "Unlimited tickets"}{p.duration_days ? ` / ${p.duration_days}d` : ""}</Feature>
                  {p.sla_hours && <Feature>~{p.sla_hours}h SLA response</Feature>}
                  {catsFor(p.id).map(c => <Feature key={c.id}>{c.name}</Feature>)}
                </ul>
                <Button className="w-full" disabled={mine || p.kind === "free"} variant={mine ? "secondary" : "default"}>
                  {mine ? "Active" : p.kind === "free" ? "Default access" : "Get this plan"}
                </Button>
                {p.kind !== "free" && !mine && (
                  <p className="text-[11px] text-muted-foreground text-center">Checkout coming soon — admin can grant access for now.</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );
}

function Feature({ children }: { children: React.ReactNode }) {
  return <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-primary shrink-0" /><span>{children}</span></li>;
}

function ActiveSubscriptionCard({ sub, planName }: { sub: Sub; planName: string }) {
  const copy = () => { navigator.clipboard.writeText(sub.activation_key); toast.success("Key copied"); };
  return (
    <Card className="border-primary">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" /> Your active support — {planName}</CardTitle>
        <CardDescription>
          Activated {new Date(sub.starts_at).toLocaleDateString()}
          {sub.expires_at && ` · expires ${new Date(sub.expires_at).toLocaleDateString()}`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-center gap-2">
          <code className="flex-1 rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm font-mono">{sub.activation_key}</code>
          <Button variant="outline" size="sm" className="gap-2" onClick={copy}><Copy className="h-3.5 w-3.5" /> Copy key</Button>
        </div>
      </CardContent>
    </Card>
  );
}
